import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';
import { useRecentOrders } from 'src/hooks/useOrders';
import { usePOSStore } from 'src/states/pos';

interface MobileTabsProps {}

/**
 * Mobile-only switch between the menu grid and the recent orders list. It drives the
 * same `expandingRecentOrders` flag the desktop "Expand" link uses — `MenuItems` already
 * unmounts and `.pos-recent-orders-container--expanded` already grows when it is set —
 * so the two viewports never hold conflicting view state.
 */
const MobileTabs = React.memo((_: MobileTabsProps) => {
  const { t } = useTranslation('pos');
  const { data: recentOrders } = useRecentOrders();
  const { expandingRecentOrders, toggleExpandingRecentOrders } = usePOSStore(
    (state) => ({
      expandingRecentOrders: state.expandingRecentOrders,
      toggleExpandingRecentOrders: state.toggleExpandingRecentOrders,
    }),
    shallow
  );

  const handleSelect = (orders: boolean) => () => {
    if (orders !== expandingRecentOrders) {
      toggleExpandingRecentOrders();
    }
  };

  return (
    <div className="pos-mobile-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={!expandingRecentOrders}
        className={classNames({ 'pos-mobile-tabs__tab': true, 'pos-mobile-tabs__tab--active': !expandingRecentOrders })}
        onClick={handleSelect(false)}
      >
        {t('menu')}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={expandingRecentOrders}
        className={classNames({ 'pos-mobile-tabs__tab': true, 'pos-mobile-tabs__tab--active': expandingRecentOrders })}
        onClick={handleSelect(true)}
      >
        {t('recentOrders')} ({recentOrders?.data?.length || 0})
      </button>
    </div>
  );
});

export default MobileTabs;
